import { useMemo } from 'react'
import { CollectedData } from 'src/interfaces/collectedData'
import {
  CollectedDataStatus,
  CollectedDataApprovalType,
  CollectedDataStepType,
  isApprovedCollectedDataStatus,
} from 'src/constants/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import { OnboardingStatus } from 'src/constants/onboarding/enums'
import { getHasStepData, isAwaitingInternalApprovalAfterCustomer } from 'src/utils/collectedData'

const STEP_STATUSES_TO_SHOW_DATA = [
  CollectedDataStatus.Completed,
  CollectedDataStatus.CompletedNotActive,
  CollectedDataStatus.Expired,
  CollectedDataStatus.Failed,
  CollectedDataStatus.WaitingForApproval,
  CollectedDataStatus.InProgress,
]

const STEP_STATUSES_TO_SHOW_EXPIRATION_EFFECTIVE = [
  CollectedDataStatus.Completed,
  CollectedDataStatus.CompletedNotActive,
  CollectedDataStatus.Expired,
  CollectedDataStatus.Failed,
]

/** Pure function version for use in loops (e.g. grouped steps) */
export function getContractorStepDetailsBlocksVisibility({
  collectedData,
  contractorDetails,
  stepCanBeRecollected = false,
  stepStatus,
  partnerId,
  isRequestApprove = false,
  isSubcontractor = false,
}: ContractorStepDetailsBlocksVisibilityParams): ContractorStepDetailsBlocksVisibility {
  const recollecting = collectedData?.isRecollecting

  if (!collectedData || !contractorDetails) {
    return {
      showApproveDecline: false,
      showRecollectButton: false,
      showDeclineButton: false,
      showSetExpirationButton: false,
      showSetEffectiveDateButton: false,
      showExpirationDate: false,
      showEffectiveDate: false,
      isWaitingForApproval: false,
      hideActions: false,
      hasStepData: false,
      showStepDataComponent: false,
      showDetails: false,
    }
  }

  const isCustomerApproval =
    collectedData.collectDataStep.approvalType === CollectedDataApprovalType.CustomerApproval ||
    collectedData.collectDataStep.approvalType ===
      CollectedDataApprovalType.InternalThenCustomerApproval ||
    collectedData.collectDataStep.approvalType ===
      CollectedDataApprovalType.CustomerThenInternalApproval

  const hasStepData =
    stepStatus != null
      ? getHasStepData(collectedData, contractorDetails, partnerId ?? undefined, stepStatus)
      : false

  const isWaitingForInternalAfterCustomer = isAwaitingInternalApprovalAfterCustomer(collectedData)

  const showDetails =
    collectedData.status === CollectedDataStatus.WaitingForApproval ||
    (isWaitingForInternalAfterCustomer && (hasStepData || !!collectedData.documents?.length)) ||
    (STEP_STATUSES_TO_SHOW_DATA.includes(collectedData.status) &&
      (hasStepData || !!collectedData.documents?.length)) ||
    !!collectedData?.lastRejectionDocuments?.length

  const isWaitingForApproval =
    collectedData.status === CollectedDataStatus.WaitingForApproval && !isRequestApprove

  const showApproveDecline =
    !showDetails &&
    !isRequestApprove &&
    !isApprovedCollectedDataStatus(collectedData.status) &&
    contractorDetails.collectedDataStatus !== OnboardingStatus.Completed

  const showRecollectButton =
    !recollecting &&
    !isRequestApprove &&
    (contractorDetails.collectedDataStatus === OnboardingStatus.Completed ||
      stepCanBeRecollected) &&
    !isSubcontractor

  const showDeclineButton =
    !isRequestApprove &&
    contractorDetails.collectedDataStatus !== OnboardingStatus.Completed &&
    contractorDetails.collectedDataStatus !== OnboardingStatus.Failed &&
    isApprovedCollectedDataStatus(collectedData.status) &&
    !contractorDetails.onboardedAt

  const showSetExpirationButton =
    !isRequestApprove &&
    isApprovedCollectedDataStatus(collectedData.status) &&
    isCustomerApproval &&
    collectedData.collectDataStep.type !== CollectedDataStepType.LmsTraining

  const showSetEffectiveDateButton =
    !isRequestApprove && isApprovedCollectedDataStatus(collectedData.status) && isCustomerApproval

  const showExpirationAndEffectiveDate =
    STEP_STATUSES_TO_SHOW_EXPIRATION_EFFECTIVE.includes(collectedData.status) && isCustomerApproval

  const showExpirationDate =
    !isRequestApprove && (showExpirationAndEffectiveDate || !!collectedData.expiresAt)

  const showEffectiveDate =
    !isRequestApprove &&
    collectedData.status !== CollectedDataStatus.Completed &&
    (showExpirationAndEffectiveDate || !!collectedData.effectiveAt)

  const hideActions = !!(recollecting && !showDetails)

  return {
    showApproveDecline,
    showRecollectButton,
    showDeclineButton,
    showSetExpirationButton,
    showSetEffectiveDateButton,
    showExpirationDate,
    showEffectiveDate,
    isWaitingForApproval,
    hideActions,
    hasStepData,
    showStepDataComponent: hasStepData,
    showDetails,
  }
}

export interface ContractorStepDetailsBlocksVisibilityParams {
  collectedData?: CollectedData | null
  contractorDetails?: ContractorDetails | null
  stepCanBeRecollected?: boolean
  stepStatus?: CollectedDataStatus
  partnerId?: string | null
  isRequestApprove?: boolean
  isSubcontractor?: boolean
}

export interface ContractorStepDetailsBlocksVisibility {
  showApproveDecline: boolean
  showRecollectButton: boolean
  showDeclineButton: boolean
  showSetExpirationButton: boolean
  showSetEffectiveDateButton: boolean
  showExpirationDate: boolean
  showEffectiveDate: boolean
  isWaitingForApproval: boolean
  hideActions: boolean
  hasStepData: boolean
  showStepDataComponent: boolean
  showDetails: boolean
}

export function useContractorStepDetailsBlocksVisibility({
  collectedData,
  contractorDetails,
  stepCanBeRecollected = false,
  stepStatus,
  partnerId,
  isRequestApprove = false,
  isSubcontractor = false,
}: ContractorStepDetailsBlocksVisibilityParams): ContractorStepDetailsBlocksVisibility {
  return useMemo(
    () =>
      getContractorStepDetailsBlocksVisibility({
        collectedData,
        contractorDetails,
        stepCanBeRecollected,
        stepStatus,
        partnerId,
        isRequestApprove,
        isSubcontractor,
      }),
    [
      collectedData,
      contractorDetails,
      stepCanBeRecollected,
      stepStatus,
      partnerId,
      isRequestApprove,
      isSubcontractor,
    ],
  )
}
